import React, { useEffect } from "react";
import AWS from "aws-sdk";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import Div from "@components/Div";
import Sidebar from "@components/Sidebar/Sidemenu";
import Header, { Button } from "@components/Header/Header";
import { H1, H3 } from "@components/Heading";
import {
  NAV_MENU_ITEMS,
  NAV_BOTTOM_MENU_ITEMS,
  HEADER_MENU_ITEMS,
  ROUTES,
  AWS_CREDENTIALS,
} from "@utils/constant";
import authRefreshService from "@app/services/auth/authRefreshservice";
import Breadcrumb from "@components/Breadcrumb";
import { getAWSCredentials } from "@utils/utils";
import AuthGuard from "./AuthGuard";
import SidebarTabMenu from "./TabMenu";
import WizardHeader from "@components/WizardHeader";
import Icon from "@components/Icon";
import styled from "styled-components";
import { breakpoints } from "@utils/breakpoints";

const { tabletLandscape, desktopLarge } = breakpoints;

const breakpoint = {
  tablet: `(min-width: ${tabletLandscape}px) and (max-width: ${desktopLarge}px)`,
};

const StyledDiv = styled(Div)`
  @media ${breakpoint.tablet} {
    max-width: calc(98vw - 190px);
  }
`;

const UserLayout = ({ children, isWizard }) => {
  const cognitoidentity = new AWS.CognitoIdentity({ region: "eu-north-1" });

  const history = useHistory();
  const { messages } = useIntl();

  const handleTabChange = (e) => {
    const { index = 0 } = e;

    switch (index) {
      case 0:
        return history.push(ROUTES.HOME.URL);
      case 1:
        return history.push(ROUTES.MY_INSURANCE.URL);
      case 2:
        return history.push(ROUTES.FONDTORG.URL);
      case 3:
        return history.push(ROUTES.FUND_PORTFOLIOS.URL);
    }
  };

  const { location = {} } = history || {};
  const { pathname = "" } = location || {};
  const query = new URLSearchParams(location.search);
  const isWizardDetailPage = query.get("isWizard");

  const getActiveIndex = () => {
    switch (pathname) {
      case ROUTES.HOME.URL:
        return 0;
      case ROUTES.MY_INSURANCE.URL:
      case ROUTES.MY_INSURANCE_DETAILS.URL:
        return 1;
      case ROUTES.FONDTORG.URL:
      case ROUTES.FUND_DETAILS.URL:
        return 2;
      case ROUTES.FUND_PORTFOLIOS.URL:
      case ROUTES.MY_FUND_PORTFOLIO_DETAILS.URL:
        return 3;
      default:
        return 0;
    }
  };

  const sidebarConfig = {
    navItems: {
      navMenuItems: NAV_MENU_ITEMS,
      navBottomMenuItems: NAV_BOTTOM_MENU_ITEMS,
    },
    visibility: true,
  };
  const storage = window.localStorage;

  const content = [
    {
      title: messages.home,
      icon: " icon ph-house-bold",
    },
    {
      title: messages.title_tab_insurance,
      icon: " icon ph-briefcase-bold",
    },
    {
      title: messages.fondtorg,
      icon: " icon ph-chart-bar-bold",
    },
    {
      title: messages.fund_portfolios,
      icon: " icon ph-squares-four-bold",
    },
  ];

  const {
    headerTitle = "",
    isDetailPage = false,
    breadcrumbItems = [],
  } = useSelector((state) => state.headerReducer);

  useEffect(() => {
    const setLocalStorage = (key, value) => {
      return storage.setItem(key, JSON.stringify(value));
    };

    const refreshAuth = async () => {
      try {
        getAWSCredentials().then(async (aws) => {
          const response = await authRefreshService(aws);
          const { epoch, Token = "", IdentityId = "" } = response;
          setLocalStorage("bfs-epoch", epoch);
          let params = {
            IdentityId: IdentityId,
            Logins: {
              "cognito-identity.amazonaws.com": Token,
            },
          };

          cognitoidentity.getCredentialsForIdentity(params, (err, data) => {
            if (err) {
              //catch error
            } else {
              const { Credentials } = data;
              const { AccessKeyId, SecretKey, SessionToken } = Credentials;

              setLocalStorage(AWS_CREDENTIALS, {
                accessKeyId: AccessKeyId,
                secretAccessKey: SecretKey,
                sessionToken: SessionToken,
              });
            }
          });
        });
      } catch (e) {
        //catch the error
      }
    };
    refreshAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <AuthGuard>
      {isWizard ? (
        <Div
          display="flex"
          minHeight="100vh"
          alignItems="center"
          flexDirection="column"
          width={1}
          bg="var(--bg-white)"
        >
          <Div
            maxWidth={["100%", "100%", "100%", 1200]}
            width={1}
            position="absolute"
            bg="var(--bg-white)"
            px={[0, 0, 0, "var(--sm-spacing)"]}
          >
            <WizardHeader />
            {isWizardDetailPage &&
              pathname === ROUTES.WIZARD_FUND_PORTFOLIO_DETAILS.URL && (
                <>
                  <Div
                    mt="var(--md-spacing)"
                    mx="var(--md-spacing)"
                    mb="var(--xs-spacing)"
                  >
                    {isDetailPage && (
                      <Div display="flex">
                        <Button
                          mr="var(--md-spacing)"
                          onClick={handleGoBack}
                          isDetailedPage
                        >
                          <Icon
                            className="icon ph-arrow-left-bold"
                            color="var(--text)"
                            fontSize="var(--fs-h5)"
                          />
                        </Button>
                        <H1>{headerTitle}</H1>
                      </Div>
                    )}
                  </Div>

                  {isDetailPage && (
                    <Div ml="var(--md-spacing)" mt="var(--md-spacing)">
                      <Breadcrumb items={breadcrumbItems} />
                    </Div>
                  )}
                </>
              )}
            {children}
          </Div>
        </Div>
      ) : (
        <Div display="flex" minHeight="100vh" width={1} bg="var(--bg-white)">
          <Sidebar
            config={sidebarConfig}
            display={[
              "none !important",
              "none !important",
              "flex !important",
              "flex !important",
            ]}
          />
          <StyledDiv
            maxWidth={["100%", "100%", "100%", "1250px"]}
            width={1}
            position="absolute"
            left={[0, 0, 0, 190]}
          >
            <Header menuItems={HEADER_MENU_ITEMS} />

            <Div
              mt="var(--md-spacing)"
              mx="var(--md-spacing)"
              mb="var(--xs-spacing)"
              alignItems="center"
              display={["flex", "none", "none", "none"]}
            >
              {isDetailPage && (
                <>
                  <Button
                    mr="var(--md-spacing)"
                    onClick={handleGoBack}
                    isDetailedPage
                  >
                    <Icon
                      className="icon ph-arrow-left-bold"
                      color="var(--text)"
                      fontSize="var(--fs-h5)"
                    />
                  </Button>
                  <H3>{headerTitle}</H3>
                </>
              )}
              {!isDetailPage && <H1>{headerTitle}</H1>}
            </Div>

            {isDetailPage && (
              <Div ml="var(--md-spacing)" mt="var(--md-spacing)">
                <Breadcrumb items={breadcrumbItems} />
              </Div>
            )}

            <Div mb={["100px", "100px", 0, 0]}>{children}</Div>
            <SidebarTabMenu
              width="100%"
              activeIndex={getActiveIndex()}
              onTabChange={handleTabChange}
              display={["inline-grid", "inline-grid", "none", "none"]}
              content={content}
            />
          </StyledDiv>
        </Div>
      )}
    </AuthGuard>
  );
};

UserLayout.propTypes = {
  children: PropTypes.node,
  isWizard: PropTypes.bool,
};
export default UserLayout;
